(() => {
  if (customElements.get('trimd-film')) return;
  class TrimdFilm extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.playButton = this.querySelector('[data-toggle-play]');
      this.soundButton = this.querySelector('[data-toggle-sound]');
      this.playLabel = this.querySelector('[data-play-label]');
      this.soundLabel = this.querySelector('[data-sound-label]');
      this.clock = this.querySelector('[data-film-time]');
      this.status = this.querySelector('[data-film-status]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.auto = this.dataset.autoplay === 'true';
      this.userPaused = false;
      this.inView = false;
      this.autoPausing = false;
      // Native controls remain the no-JavaScript fallback.
      this.video.controls = this.dataset.nativeControls === 'true';
      this.video.muted = true;
      if (this.video.dataset.desktop) {
        this.video.src = matchMedia('(max-width: 749px)').matches
          ? this.video.dataset.mobile || this.video.dataset.desktop
          : this.video.dataset.desktop;
      }
      this.querySelector('[data-film-toolbar]')?.removeAttribute('hidden');
      if (this.video.controls) this.querySelector('[data-film-controls]')?.setAttribute('hidden', '');
      this.playButton?.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          if (this.video.ended) this.video.currentTime = 0;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }, options);
      this.soundButton?.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        if (!this.video.muted && this.video.paused) {
          this.userPaused = false;
          this.play();
        }
        this.sync();
      }, options);
      this.video.addEventListener('play', () => {
        this.userPaused = false;
        this.status.hidden = true;
        this.sync();
      }, options);
      this.video.addEventListener('pause', () => {
        if (!this.autoPausing && this.inView && !document.hidden) this.userPaused = true;
        this.autoPausing = false;
        this.sync();
      }, options);
      ['ended', 'volumechange', 'loadedmetadata', 'timeupdate'].forEach(event => this.video.addEventListener(event, () => this.sync(), options));
      this.video.addEventListener('error', () => {
        this.status.textContent = this.dataset.errorLabel;
        this.status.hidden = false;
        this.video.controls = true;
      }, options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      this.reduced.addEventListener('change', () => this.updatePlayback(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .25;
          this.updatePlayback();
        }, { threshold: [0, .25] });
        this.observer.observe(this.querySelector('.trimd-film__frame'));
      }
      this.sync();
    }
    play() {
      this.video.play().catch(() => {
        // Low power mode and autoplay restrictions still allow manual playback.
        this.sync();
      });
    }
    updatePlayback() {
      if (document.hidden || !this.inView || this.reduced.matches) {
        if (!this.video.paused) {
          this.autoPausing = true;
          this.video.pause();
        }
      } else if (this.auto && !this.userPaused) {
        this.play();
      }
    }
    sync() {
      const playing = !this.video.paused && !this.video.ended;
      this.dataset.playing = String(playing);
      this.dataset.muted = String(this.video.muted);
      const playLabel = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
      this.playButton?.setAttribute('aria-label', playLabel);
      if (this.playLabel) this.playLabel.textContent = playLabel;
      this.soundButton?.setAttribute('aria-label', this.video.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel);
      this.soundButton?.setAttribute('aria-pressed', String(!this.video.muted));
      if (this.soundLabel) this.soundLabel.textContent = this.video.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel;
      if (this.clock && Number.isFinite(this.video.duration)) {
        const format = value => `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;
        this.clock.textContent = `${format(this.video.currentTime)} / ${format(this.video.duration)}`;
      }
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.observer?.disconnect();
      this.video?.pause();
      this.abort = null;
    }
  }
  customElements.define('trimd-film', TrimdFilm);
})();
